import { useEffect, useMemo } from 'react'

import { FormProvider, useForm } from 'react-hook-form'

import { Separator } from '@/components/ui/separator.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { FanSpendColorTable } from '@/modules/display-settings/ui/color/fan-spend-color-table.tsx'
import { DisplaySettingsEmojis } from '@/modules/display-settings/ui/display-settings-emojis.tsx'
import { DisplaySettingsSkeleton } from '@/modules/display-settings/ui/display-settings-skeleton.tsx'
import { CountdownAudio } from '@/modules/display-settings/ui/fan-audio.tsx'
import type { DisplaySettingsFormValues } from '@/modules/display-settings/ui/validation.ts'
import { transformSpendColorsToArray } from '@/modules/display-settings/utils.ts'

import { DisplaySettingsCustomNotePreset } from './display-settings-custom-note-preset'

export const DisplaySettings = () => {
  const {
    colors,
    defaultColor,
    countdownSoundType,
    countdownSoundVolume,
    emojiTaskbarEnabled,
    emojiTaskbar,
    isFetching,
    customNotePresetEnabled,
    customNotePreset,
  } = useSettingsContext()

  const colorsData = transformSpendColorsToArray(defaultColor!, colors)

  const defaultValues = useMemo(
    () => ({
      colors: colorsData,
      audioCountdownSound: countdownSoundType,
      audioCountdownVolume: [countdownSoundVolume],
      showEmojis: emojiTaskbarEnabled,
      emojis: emojiTaskbar,
      customNotePresetEnabled,
      customNotePreset,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      colors,
      countdownSoundType,
      countdownSoundVolume,
      emojiTaskbarEnabled,
      emojiTaskbar,
      customNotePresetEnabled,
      customNotePreset,
    ],
  )

  const form = useForm<DisplaySettingsFormValues>({
    defaultValues,
  })

  // react-hook-form caches default values internal, so we have to reset them..
  useEffect(
    () => {
      form.reset(defaultValues)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.reset, defaultValues],
  )

  const noop = () => {
    // Todo: refactor hook-form needs form in order to create context idk
  }

  if (!colors || isFetching) {
    return <DisplaySettingsSkeleton />
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(noop)}>
        <div className="mt-8 flex flex-col gap-[32px]">
          <FanSpendColorTable />
          <Separator />
          <CountdownAudio />
          <Separator />
          <DisplaySettingsEmojis />
          <Separator />
          <DisplaySettingsCustomNotePreset />
        </div>
      </form>
    </FormProvider>
  )
}
